import { apiClient } from '../utils/api';
import { UrlBuilder } from '../utils/urls';
import { User } from '../models/User';
import { set } from 'date-fns';


export interface LoginCredentials {
  email: string;
  password: string;
}

export interface SignupCredentials {
  username: string;
  email: string;
  password: string;
  password_confirmation: string;
  verification_code?: string;
  google_token?: string;
  is_google_auth?: boolean;
  firstname?: string | null;
  lastname?: string | null;
  image_url?: string | null;
}

export interface GoogleSignupCredentials {
  google_token: string;
  username?: string;
  email?: string;
  firstname?: string | null;
  lastname?: string | null;
  image_url?: string | null;
}

export interface UpdateUserData {
  email?: string;
  password?: string;
  password_confirmation?: string;
  code?: string;
  username?: string;
  firstname?: string | null;
  lastname?: string | null;
  image_url?: string | null;
}

export interface ResetPasswordWithCodeData {
  email: string;
  code: string;
  password: string;
  password_confirmation: string;
}

export interface AuthResponse {
  access_token: string;
  user: {
    id: number;
    username: string;
    email: string;
    image_url?: string;
    first_name?: string;
    last_name?: string;
  };
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  code?: number;
}

export class AuthService {
  useAppContext: Function;
  constructor(useAppContext: Function) {
    this.useAppContext = useAppContext;
  }
  
  // Login with email and password (no token needed)
  async login(credentials: LoginCredentials): Promise<ApiResponse<AuthResponse>> {
    const { setToken } = this.useAppContext();
    try {
      const response = await apiClient.post<AuthResponse>(
        UrlBuilder.user.login(),
        credentials
      );

      if (response.success) {
        const { access_token, user } = response.data;
        
        // Save token to cookies
        setToken?.(access_token);
        
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Signup with email and password (no token needed)
  async signup(credentials: SignupCredentials): Promise<ApiResponse<{ user: any }>> {
    try {
      const response = await apiClient.post<{ user: any }>(
        UrlBuilder.user.register(),
        credentials
      );

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Signup with Google (no token needed)
  async signupWithGoogle(credentials: GoogleSignupCredentials): Promise<ApiResponse<AuthResponse>> {
    const { setToken } = this.useAppContext();
    try {
      // Prepare the signup payload for Google authentication
      const signupPayload: SignupCredentials = {
        username: credentials.username || `user_${Date.now()}`,
        email: credentials.email || '',
        password: 'google_auth_password', // Placeholder password for Google auth
        password_confirmation: 'google_auth_password',
        google_token: credentials.google_token,
        is_google_auth: true,
        firstname: credentials.firstname || null,
        lastname: credentials.lastname || null,
        image_url: credentials.image_url || null
      };

      const response = await apiClient.post<AuthResponse>(
        UrlBuilder.user.register(),
        signupPayload
      );

      if (response.success) {
        const { access_token, user } = response.data;
        
        // Save token to cookies
        setToken?.(access_token);
        
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Send verification code (no token needed)
  async sendVerificationCode(email: string): Promise<ApiResponse> {
    try {
      const response = await apiClient.post(
        UrlBuilder.user.sendVerification(),
        { email }
      );

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Update user (requires authentication - use token explicitly)
  async updateUser(token: string, data: UpdateUserData): Promise<ApiResponse> {
    try {
      const response = await apiClient.putWithToken(
        UrlBuilder.user.update(),
        token,
        data
      );

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Reset password with code (does not require authentication)
  async resetPasswordWithCode(data: ResetPasswordWithCodeData): Promise<ApiResponse> {
    try {
      const response = await apiClient.put(
        UrlBuilder.user.resetPasswordWithCode(),
        data
      );

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Login with Google (no token needed)
  async loginWithGoogle(googleToken: string): Promise<ApiResponse<AuthResponse>> {
    const { setToken } = this.useAppContext();
    try {
      const response = await apiClient.post<AuthResponse>(
        UrlBuilder.user.loginWithGoogle(),
        { google_token: googleToken }
      );

      if (response.success) {
        const { access_token, user } = response.data;
        
        // Save token to cookies
        setToken?.(access_token);
        
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Logout
  logout(): void {
    this.clearSession();
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const { token } = this.useAppContext();
    return !!token;
  }

  // Get current user from token (you might want to validate token with backend)
  getCurrentUser(): User | null {
    const { user } = this.useAppContext();
    return user;
  }
        

  // Save user session to cookies
  saveUserSession(user: User): void {
    const { setUser } = this.useAppContext();
    setUser?.(user);
  }

  // Clear user session
  clearSession(): void {
    const { setUser, setToken } = this.useAppContext();
    setUser?.(null);
    setToken?.(null);
  }
}
